import { useEffect, useLayoutEffect, useRef, type RefObject, type ReactNode } from "react";

type HorizontalNavScrollProps = {
  activeIndex: number;
  liRefs: RefObject<HTMLLIElement | null>[];
  items?: any[];
  children?: ReactNode;
};

export default function HorizontalNavScroll({ items = [], activeIndex = 0, liRefs, children }: HorizontalNavScrollProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const scrollToActive = (behavior: ScrollBehavior = "smooth") => {
    const container = containerRef.current;
    const el = liRefs[activeIndex]?.current;
    if (!container || !el) return;

    // 스크롤 가능 여부 점검
    const sw = container.scrollWidth;
    const cw = container.clientWidth;
    if (sw <= cw) {
      console.log("[nav] no horizontal overflow", { scrollWidth: sw, clientWidth: cw });
      return;
    }

    // padding 고려(있으면 정확히 계산)
    const cs = getComputedStyle(container);
    const padL = parseFloat(cs.paddingLeft) || 0;
    const padR = parseFloat(cs.paddingRight) || 0;
    const viewport = cw - padL - padR;

    // 요소 위치를 컨테이너 기준으로 안전 계산
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    const itemLeftInContent = eRect.left - cRect.left + container.scrollLeft - padL;
    const itemWidth = eRect.width;

    // 기본: 중앙 정렬
    // let target = itemLeftInContent - (viewport - itemWidth) / 2;
    let target = itemLeftInContent - (viewport - itemWidth) / 3;

    // 경계값
    const min = 0;
    const max = sw - cw;

    // 첫/마지막에서 두번째 메뉴 보정
    if (activeIndex === 0) target = min;
    else if (activeIndex === items.length - 1) target = max;

    // clamp
    target = Math.max(min, Math.min(target, max));

    // 실제 이동
    if (Math.abs(container.scrollLeft - target) > 1) {
      container.scrollTo({ left: target, behavior });
      console.log("[nav] scrollTo", { target, min, max, viewport });
    } else {
      console.log("[nav] already in place");
    }
  };

  useLayoutEffect(() => {
    if (!items?.length) return;

    // 레이아웃이 확정된 뒤에 계산 (폰트/리사이즈 등)
    const id = requestAnimationFrame(() => scrollToActive());
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, items.length, location.pathname]);

  // 리사이즈 대응
  useEffect(() => {
    if (!items?.length) return;

    const ro = new ResizeObserver(() => scrollToActive("auto"));
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  return (
    items?.length && (
      <div
        ref={containerRef}
        className="scr w-full overflow-x-auto border-t border-gray-100 whitespace-nowrap"
        style={{
          scrollbarWidth: "none", // Firefox
          msOverflowStyle: "none", // IE/Edge
        }}
      >
        <ul className="flex items-center px-[10px]">{children}</ul>
      </div>
    )
  );
}
